// uploadPinPhotos — sequential multi-photo upload for a pin clue.
//
// 2026-05-30: workers can attach multiple clue photos to a pin (when placing
// or appending later from the edit panel). Mirrors uploadFragmentPhotos: each
// photo gets a crypto.randomUUID() filename so individual photos can be
// reordered/deleted later without path collision.
//
// Path: pins-photos/{userId_regionId}/{pinId}/{uuid}.jpg
//   — note the extra /{pinId}/ folder vs the legacy single-file
//     pins-photos/{userId_regionId}/{pinId}.jpg. The storage.rules
//     recursive {allPaths=**} wildcard matches both.
//
// Returns download URLs + storage paths in input order. On any photo failure,
// throws — caller decides whether to surface a soft error (the new-pin flow
// records the failure per learner; the edit panel shows an inline error).

import { getFirebase, markerDocId } from "@goodbye/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { resizeImageToBlob } from "./resizeImageToBlob";

export interface UploadPinPhotosInput {
  userId: string;
  regionId: string;
  pinId: string;
  files: File[];
}

export interface UploadPinPhotosResult {
  photoUrls: string[];
  /** Storage paths in same order, for cleanup-on-fail. */
  photoStoragePaths: string[];
}

export async function uploadPinPhotos(
  input: UploadPinPhotosInput,
): Promise<UploadPinPhotosResult> {
  const { userId, regionId, pinId, files } = input;
  const storage = getFirebase().storage;
  const marker = markerDocId(userId, regionId);
  const photoUrls: string[] = [];
  const photoStoragePaths: string[] = [];

  for (const file of files) {
    const uuid =
      typeof crypto?.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const path = `pins-photos/${marker}/${pinId}/${uuid}.jpg`;
    const blob = await resizeImageToBlob(file);
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob, { contentType: "image/jpeg" });
    const url = await getDownloadURL(storageRef);
    photoUrls.push(url);
    photoStoragePaths.push(path);
  }

  return { photoUrls, photoStoragePaths };
}
